import { listTeams, getTeam, listTeamMembers } from "./api/teams-api.js";
import {
  useMemo,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EmptyState } from "@playbookai/ui";
import {
  ApiClientProvider,
  type ApiClientConfig,
} from "@playbookai/api-clients";
import { WidgetEntityResolverWiring } from "@playbookai/solve-feature";
import { KpiWidgetWiring } from "./widgets/KpiWidgetWiring";
import { CollabProvider } from "@playbookai/collab-client";
import { PlayerAIProvider } from "@playbookai/player-ai-client";
import { CallbackPage } from "./auth/CallbackPage.js";
import { RestartSignInPage } from "./auth/RestartSignInPage.js";
import { SignOutPage } from "./auth/SignOutPage.js";
import { IntegrationOauthComplete } from "./pages/oauth/IntegrationOauthComplete.js";
import { InvitePage } from "./pages/invite/InvitePage.js";
import { SetPasswordPage } from "./pages/set-password/SetPasswordPage.js";
import { legacyCollabTicket } from "./auth/legacy-collab-ticket.js";
import { RequireAuth } from "./auth/RequireAuth.js";
import { RequireTenantAdmin } from "./auth/RequireTenantAdmin.js";
import { ME_QUERY_KEY, useAuthStore } from "./auth/auth-store.js";
import {
  authScopeSource,
  canAdministerWorkspaceSource,
  tenantRolesSource,
} from "./auth/auth-scope.js";
import {
  getCsrfToken,
  mintCollabWsTicket,
  reportCollabClientEvent,
  refreshSession,
  RefreshSessionError,
} from "./auth/session-api.js";
import { createModuleUnavailableRefresh } from "./auth/module-unavailable-refresh.js";
import { AppLayout } from "./layout/AppLayout.js";
import { ModuleRouteBoundary } from "./modules/ModuleRouteBoundary.js";
import TtiRoute from "./modules/TtiRoute.js";
import { IndexRoute } from "./pages/IndexRoute.js";
import { Launcher } from "./pages/Launcher.js";
import { Notifications } from "./pages/Notifications.js";
import { TeamDetailPage } from "./pages/teams/TeamDetailPage.js";
import { TeamsPage } from "./pages/teams/TeamsPage.js";
import { PlayerAIPage } from "./pages/PlayerAIPage.js";
import { SettingsPage } from "./pages/settings/SettingsPage.js";
import { AccountSettings } from "./pages/settings/AccountSettings.js";
import { LauncherSettings } from "./pages/settings/LauncherSettings.js";
import { NotificationsSettings } from "./pages/settings/NotificationsSettings.js";
import { ConnectionToolsSettings } from "./pages/settings/ConnectionToolsSettings.js";
import { PlayerAIConnectedApps } from "./pages/settings/PlayerAIConnectedApps.js";
import { PlayerAIGeneralSettings } from "./pages/settings/PlayerAIGeneralSettings.js";
import { PlayerAISettings } from "./pages/settings/PlayerAISettings.js";
import { KpiSettings } from "./pages/settings/KpiSettings.js";
import { IntegrationsSettings } from "./pages/admin/integrations/IntegrationsSettings.js";
import { AddIntegration } from "./pages/admin/integrations/AddIntegration.js";
import { ConnectionsSettings } from "./pages/settings/ConnectionsSettings.js";
import { AddConnectionSettings } from "./pages/settings/AddConnectionSettings.js";
import { AdminPage } from "./pages/admin/AdminPage.js";
import { MembersAdmin } from "./pages/admin/MembersAdmin.js";
import { OrganizationAdmin } from "./pages/admin/OrganizationAdmin.js";
import { IdentityAdmin } from "./pages/admin/IdentityAdmin.js";
import { DirectoryGroupRoles } from "./pages/admin/DirectoryGroupRoles.js";
import { DirectoryGroupMembershipAdmin } from "./pages/admin/DirectoryGroupMembershipAdmin.js";
import { ModulesAdmin } from "./pages/admin/ModulesAdmin.js";
import { CommunicationAdmin } from "./pages/admin/CommunicationAdmin.js";
import { AuditAdmin } from "./pages/admin/AuditAdmin.js";
import { GenAiAdmin } from "./pages/admin/GenAiAdmin.js";
import { HealthAdmin } from "./pages/admin/HealthAdmin.js";
import {
  ADMIN_INTEGRATIONS_ROUTE,
  ADMIN_MEMBERS_ROUTE,
  TEAMS_ROUTE,
} from "./routes.js";
import { getEnabledModules, isModuleAvailable } from "./modules/index.js";
import { moduleAvailabilitySource } from "./modules/availability.js";
import { NotFoundRoute } from "./modules/NotFoundRoute.js";
import {
  getCollaborationProtocolMode,
  getCollaborationWsUrl,
} from "./config/runtime.js";
import { usePlayerAiPollFallback } from "./player-ai/usePlayerAiChatRecovery.js";
import type { SecureCollabProtocolMode } from "@playbookai/collab-client";

/**
 * Root component.
 *
 * Auth has migrated from a browser-side OIDC PKCE flow (ADR-0011) to a
 * server-side BFF in `services/auth` (ADR-0013, supersedes ADR-0011).
 * The browser sees only an HttpOnly `pb_session` cookie; tokens stay in
 * the BFF's Redis-backed session store. The SPA discovers its own session
 * via `GET /api/auth/me`, which is what `useAuthStore` is now built on.
 *
 * `RequireAuth` redirects to `/api/auth/login` when no session exists;
 * `CallbackPage` completes the BFF callback and resumes routing.
 *
 * `QueryClientProvider` is mounted at the top — `useAuthStore` uses
 * TanStack Query under the hood for /me, and feature packages share the
 * same client.
 */
export function App(): ReactElement {
  // This value is intentionally strict. Missing or invalid deploy-time
  // configuration must fail before CollabProvider mounts.
  let collaborationProtocolMode: SecureCollabProtocolMode;
  try {
    collaborationProtocolMode = getCollaborationProtocolMode();
  } catch {
    return <CollaborationConfigurationUnavailable />;
  }

  return (
    <ConfiguredApp collaborationProtocolMode={collaborationProtocolMode} />
  );
}

function CollaborationConfigurationUnavailable(): ReactElement {
  return (
    <main className="flex min-h-screen items-center justify-center bg-pb-page px-6">
      {/* The page-level error screen: EmptyState lg (OK-33) in today's
          frame. The h1 stays this page's own, inside the EmptyState title.
          The frame is kept hand-built: Card takes a 14px corner, an 80%
          border and its 1px inset top hairline, a look change no PR 3 row
          approves. */}
      <section
        aria-labelledby="collaboration-configuration-unavailable"
        className="w-full max-w-lg rounded-lg border border-pb-border bg-pb-card"
        role="alert"
      >
        <EmptyState
          variant="error"
          size="lg"
          title={
            <h1 id="collaboration-configuration-unavailable">
              Collaboration configuration unavailable
            </h1>
          }
          description="This environment is missing a valid collaboration mode. Contact your admin before retrying."
        />
      </section>
    </main>
  );
}

function ConfiguredApp({
  collaborationProtocolMode,
}: {
  readonly collaborationProtocolMode: SecureCollabProtocolMode;
}): ReactElement {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [],
  );

  const playerAiBaseUrl = useMemo(
    () => import.meta.env.VITE_PLAYER_AI_URL ?? "/api/player-ai",
    [],
  );
  // `pollFallback` is added below the QueryClientProvider, in
  // SessionPlayerAIProvider, because it reads the tenant's setting.
  const playerAiOptions = useMemo(
    () => ({
      baseUrl: playerAiBaseUrl,
      getCsrfToken: () => getCsrfToken(),
    }),
    [playerAiBaseUrl],
  );

  // BFF posture: same-origin reverse proxy at /api/v1. The dev value of
  // VITE_PLAYBOOK_BE_URL falls back to a same-origin path so the dev SPA
  // talks to the local docker-compose'd BFF.
  const apiClientConfig: ApiClientConfig = useMemo(
    () => ({
      baseUrl: import.meta.env.VITE_PLAYBOOK_BE_URL ?? "/api",
      // No Bearer in BFF mode — the session cookie does the auth.
      getAccessToken: () => null,
      authScope: authScopeSource,
      accessTeams: {
        search: (query, cursor) =>
          listTeams({ scope: "mine", query, ...(cursor ? { cursor } : {}) }),
        getTeam,
        members: (id, cursor) => listTeamMembers(id, cursor ? { cursor } : {}),
      },
      canAdministerWorkspace: canAdministerWorkspaceSource,
      // ADR-0078: the realm roles, for LAMA's `transformation-lead` gate.
      tenantRoles: tenantRolesSource,
      // ADR-0100: which modules this workspace may use, so features hide
      // link options, widgets and Open for a module that is off.
      moduleAvailability: moduleAvailabilitySource,
      // Mirror the in-memory CSRF token (set by /me + /callback responses)
      // onto every state-changing request.
      getCsrfToken: () => getCsrfToken(),
      useCookies: true,
      getCurrentUser: () => {
        const claims = useAuthStore.getState().claims;
        if (!claims) return null;
        // Same fallback rung as the chrome (PB-5208). Collaboration publishes
        // this into canvas awareness and `usePublishCanvasPresence` resolves a
        // null name to the raw user id — so without the email here, a user with
        // no display name shows peers a UUID as their cursor label.
        return { id: claims.userId, name: claims.name ?? claims.email };
      },
      // 401-retry path. Drives the BFF's silent renew over the cookie;
      // returns null because there's no Bearer to hand back.
      refreshAccessToken: async () => {
        try {
          await refreshSession();
        } catch (err) {
          // Only a definitive rejection ends the session. A stale CSRF token,
          // an auth-service 5xx or a dropped connection say nothing about
          // whether the session is still good, and signing every user out on a
          // blip is far worse than one failed request (PB-4900).
          if (err instanceof RefreshSessionError && err.kind === "terminal") {
            // Drive the store's own derivation rather than bypassing it: a null
            // /me is precisely what "unauthenticated" means here, and
            // RequireAuth takes it from there with `returnTo`.
            queryClient.setQueryData(ME_QUERY_KEY, null);
          }
          // Rethrow either way — `apiCall` retries only when this resolves.
          throw err;
        }
        // Renewed. There is no bearer to hand back in BFF mode; the browser
        // re-attaches the cookie itself, and resolving is the retry signal.
        return null;
      },
      // ADR-0100 (AC-10): a refusal for a module that is off means this
      // session's module list is stale. Refresh first, then reload /me.
      onModuleUnavailable: createModuleUnavailableRefresh({
        refreshSession,
        reloadMe: () =>
          queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
      }),
    }),
    [queryClient],
  );

  // Picked when the page loads, never at build time, so one image works at
  // every site (PB-5510).
  const collabWsUrl = useMemo(() => getCollaborationWsUrl(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientProvider value={apiClientConfig}>
        <WidgetEntityResolverWiring>
          <KpiWidgetWiring>
            <CollaborationRuntime
              wsUrl={collabWsUrl}
              protocolMode={collaborationProtocolMode}
            >
              <SessionPlayerAIProvider options={playerAiOptions}>
                <AppRoutes />
              </SessionPlayerAIProvider>
            </CollaborationRuntime>
          </KpiWidgetWiring>
        </WidgetEntityResolverWiring>
      </ApiClientProvider>
    </QueryClientProvider>
  );
}

function CollaborationRuntime({
  wsUrl,
  protocolMode,
  children,
}: {
  readonly wsUrl: string;
  readonly protocolMode: SecureCollabProtocolMode;
  readonly children: ReactNode;
}): ReactElement {
  const sessionClaims = useAuthStore((state) => state.claims);
  const secureCollab = useMemo(
    () =>
      sessionClaims?.tenantId
        ? {
            authScope: {
              tenantId: sessionClaims.tenantId,
              actorUserId: sessionClaims.userId,
              authSessionGeneration: sessionClaims.authSessionGeneration,
            },
            getTicket: mintCollabWsTicket,
            onLifecycleEvent: reportCollabClientEvent,
          }
        : undefined,
    [sessionClaims],
  );
  // `getAccessToken` is the legacy ticket path. VSM, SOLVE and Hoshin all read
  // it wherever `protocolMode` is "legacy"; it retries a busy or unreachable
  // ticket service and never turns a failure into an empty sign-in
  // (NET-BUG-28). Secure mode uses `secure.getTicket` instead.
  return (
    <CollabProvider
      wsUrl={wsUrl}
      protocolMode={protocolMode}
      {...(secureCollab ? { secure: secureCollab } : {})}
      getAccessToken={legacyCollabTicket}
    >
      {children}
    </CollabProvider>
  );
}

function AppRoutes(): ReactElement {
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );
  const modules = getEnabledModules(tenantEnabledModules);
  const hoshinAvailable = isModuleAvailable("hoshin", tenantEnabledModules);
  const kpiAvailable = isModuleAvailable("kpi", tenantEnabledModules);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/auth/callback" element={<CallbackPage />} />
        {/* PB-5461. Two more sign-in pages beside the callback, and outside
            the guard for the same reason: each is reached from the Keycloak
            login page, by a tab that has no app session or is about to lose
            it. `/auth/restart` starts a fresh sign-in to the page this tab
            kept; `/auth/sign-out` never signs out on load — it asks first, or
            hands the asking to Keycloak's own confirm page. */}
        <Route path="/auth/restart" element={<RestartSignInPage />} />
        <Route path="/auth/sign-out" element={<SignOutPage />} />
        {/* PB-1592 — the OAuth round trip's landing page, and the second route
            deliberately OUTSIDE `RequireAuth`. The provider redirects to the
            backend callback, which 303s this tab here: a plain navigation with
            no bearer token, in a tab whose session may have expired while the
            user was on the provider's consent screen. Inside the guard, that
            tab would start a fresh sign-in the user never asked for and land
            them somewhere other than the console that opened it. The page reads
            no API and holds no session — see its own header for what it may
            know. */}
        <Route
          path="/oauth/integration-complete"
          element={<IntegrationOauthComplete />}
        />
        {/* PB-4922 (AC-15). The invitation redemption page, public because the
            visitor has no account yet — that is what it exists to give them.

            TWO routes, and the second is not redundant. The catch-all below
            (`path="*"`) lives INSIDE the guarded `path="/"` element, so any path
            that fails to match a sibling here falls through to RequireAuth and
            begins a login. For an invitee that login cannot succeed, and the
            recorded consequence of retrying it is a Keycloak 429 that locks them
            out of the account they were being given.

            The second route is a SPLAT rather than the bare `/invite` it started
            as, and the difference was found in a browser, not in a test. `/invite`
            and a trailing slash were covered; `/invite/abc/def` was not — it
            matched neither sibling, fell into the guarded catch-all and drove a
            real redirect all the way to the Keycloak login page. A mangled or
            wrapped link in a mail client produces exactly that shape, so the case
            is not hypothetical. `/invite/*` matches zero or more trailing
            segments, and React Router ranks the dynamic `:token` above a splat,
            so a genuine one-segment link still lands on the redemption path. */}
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/invite/*" element={<InvitePage />} />
        {/* PB-5294. The set-password page, public for the same reason: the
            visitor has redeemed an invitation and still has no credential.

            NO `:token` SEGMENT, unlike `/invite`. The token arrives in the URL
            FRAGMENT, which the router never sees and the server never
            receives — see `SetPasswordPage` for the measurement behind that
            choice. The splat is kept for the same reason it exists above: a
            mail client that wraps or mangles a link produces trailing
            segments, and without it those fall into the guarded catch-all and
            drive a login the visitor cannot complete. A fragment survives that
            mangling, so the page still works when the splat catches it. */}
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/set-password/*" element={<SetPasswordPage />} />
        <Route
          path="/player-ai"
          element={
            <RequireAuth>
              <PlayerAIPage />
            </RequireAuth>
          }
        />
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<IndexRoute />} />
          <Route path="launcher" element={<Launcher />} />
          <Route path="launcher/:tabId" element={<Launcher />} />
          <Route path="settings" element={<SettingsPage />}>
            <Route index element={<AccountSettings />} />
            {/* General at the section URL; Connected apps at its own URL so a
                connection can link straight to it (PB-5134, d16). */}
            <Route path="player-ai" element={<PlayerAISettings />}>
              <Route index element={<PlayerAIGeneralSettings />} />
              <Route
                path="connected-apps"
                element={<PlayerAIConnectedApps />}
              />
              <Route
                path="connected-apps/:connectionId"
                element={<ConnectionToolsSettings />}
              />
            </Route>
            <Route path="launcher" element={<LauncherSettings />} />
            <Route path="notifications" element={<NotificationsSettings />} />
            {/* Personal, not admin: a member who cannot configure an
                integration must still be able to connect to one. */}
            <Route path="connections" element={<ConnectionsSettings />} />
            <Route path="connections/new" element={<AddConnectionSettings />} />
            {/* Old Admin Console homes (PB-51 / PB-181) → /admin. `replace`
                so Back doesn't bounce through the retired URL. */}
            <Route
              path="users"
              element={<Navigate to={ADMIN_MEMBERS_ROUTE} replace />}
            />
            <Route
              path="workspace"
              element={<Navigate to={ADMIN_MEMBERS_ROUTE} replace />}
            />
            {/* KPI Manager + its child pages: the dedicated Create KPI page, a
                KPI's detail page, and that KPI's edit form (PB-2941). All are
                CHILDREN of the same route element (KpiSettings renders no
                <Outlet/> — these children exist only so every KPI URL matches
                one mounted host), which keeps the KPI Manager alive across them:
                browse state survives the trip, an unsaved draft survives long
                enough to prompt before it is discarded, and browser Back from a
                detail page restores the collection exactly as it was left.

                `create` is declared before `:kpiId` for readability only —
                React Router ranks a static segment above a dynamic one
                regardless, so `/settings/kpi/create` can never be read as a KPI
                id. `:kpiId/edit` is the ONLY two-segment child; anything else
                that deep (`/settings/kpi/a/b`) still falls through to NotFound.

                DELIBERATELY NOT wrapped in RequireTenantAdmin, unlike the rest
                of this file's gated surfaces. The gate is NOT uniform across
                these three URLs since PB-2941: browsing and creating are
                Tenant-Admin, but a KPI's OWNER — routinely a plain
                tenant-member — has to reach `/settings/kpi/:kpiId` to record a
                value. A route-level gate cannot express "admin, OR the holder
                of a per-row fact this component has not fetched yet", so
                KpiSettings applies it per URL and renders the SAME
                RequireTenantAdmin denial for the two admin-only ones. The
                server is still authoritative either way.

                Mounted only while KPI Manager is available (ADR-0100), like a
                module route: without it every KPI URL falls through to the
                catch-all, which says KPI Manager is off. */}
            {kpiAvailable ? (
              <Route path="kpi" element={<KpiSettings />}>
                <Route index element={null} />
                {/* Static child views of the collection (PB-5134, d3); like
                    `create`, never read as a KPI id. */}
                <Route path="library" element={null} />
                <Route path="archived" element={null} />
                <Route path="create" element={null} />
                <Route path=":kpiId" element={null} />
                <Route path=":kpiId/edit" element={null} />
              </Route>
            ) : null}

            {/* Integrations moved to /admin/integrations (PB-4624). `replace`
                so Back doesn't bounce through the retired URL, matching the
                PB-51/PB-181 redirects above. */}
            <Route
              path="integrations"
              element={<Navigate to={ADMIN_INTEGRATIONS_ROUTE} replace />}
            />
          </Route>
          {/* Admin console (PB-42): tenant administration at a top level.
              Gated ONCE here — children inherit the gate; the server remains
              the authoritative gate. */}
          <Route
            path="admin"
            element={
              <RequireTenantAdmin area="the Admin console">
                <AdminPage />
              </RequireTenantAdmin>
            }
          >
            <Route
              index
              element={<Navigate to={ADMIN_MEMBERS_ROUTE} replace />}
            />
            <Route path="members" element={<MembersAdmin />} />
            <Route path="organization" element={<OrganizationAdmin />} />
            <Route path="identity" element={<IdentityAdmin />} />
            {/* Directory group -> tenant role rules (PB-136). Sits beside the
                identity/directory area it belongs to and inherits the gate
                above, like every other admin child. */}
            <Route
              path="directory-group-roles"
              element={<DirectoryGroupRoles />}
            />
            {/* Manual directory-group membership — TEMPORARY (ahead of
                PB-63). Sits beside its sibling above and inherits the same
                gate. Delete this route when PB-63 lands. */}
            <Route
              path="directory-group-membership"
              element={<DirectoryGroupMembershipAdmin />}
            />
            <Route path="modules" element={<ModulesAdmin />} />
            <Route path="communication" element={<CommunicationAdmin />} />
            <Route path="audit" element={<AuditAdmin />} />
            <Route path="genai" element={<GenAiAdmin />} />
            {/* Integrations (PB-4624). NO RequireTenantAdmin of its own: the
                `/admin` parent gates once and children inherit, so wrapping it
                again would double-gate. */}
            <Route path="integrations" element={<IntegrationsSettings />} />
            {/* Adding is a page, not a panel (PB-4624 follow-up): the OAuth
                round trip in that flow leaves the tab, so the step it comes
                back to has to be a URL. Declared before the id route for
                readability — React Router ranks a static segment above a
                dynamic one regardless of order, so `new` is never read as an
                integration id. */}
            <Route path="integrations/new" element={<AddIntegration />} />
            <Route
              path="integrations/:integrationId/:section?"
              element={<IntegrationsSettings />}
            />
            <Route path="health" element={<HealthAdmin />} />
          </Route>
          <Route path="notifications" element={<Notifications />} />
          {/* The Target to Improve page (PB-5317). Module-NEUTRAL by design: it
              is opened from the Hoshin bowling chart and from KPI detail, so it
              is mounted here rather than inside either module's router, and
              `/app/tti` is deliberately absent from `ALL_MODULES` so the
              deep-link bootstrap mints no instance segment and no tab for it.
              Wrapped in the module error/suspense boundary like a module route,
              because the page it lazy-loads is a feature package.
              Mounted only while Hoshin is available: the page reads Hoshin's
              APIs, so without Hoshin the URL falls through to the catch-all
              like any disabled module's. */}
          {hoshinAvailable ? (
            <Route
              path="app/tti/:ttiId"
              element={
                <ModuleRouteBoundary label="Target to Improve">
                  <TtiRoute />
                </ModuleRouteBoundary>
              }
            />
          ) : null}
          {/* Home lives at "/". Older notifications linked to "/app/home",
              which no route serves; send those to Home rather than not-found. */}
          <Route path="app/home" element={<Navigate to="/" replace />} />
          {/* Teams shares the Settings rail and remains open to every member. */}
          <Route path={TEAMS_ROUTE.slice(1)} element={<SettingsPage />}>
            <Route index element={<TeamsPage />} />
            <Route path=":teamId" element={<TeamDetailPage />} />
          </Route>
          {modules.map((m) => (
            <Route
              key={m.id}
              path={`${stripLeadingSlash(m.routeBase)}/*`}
              element={
                <ModuleRouteBoundary label={m.label}>
                  <m.Component />
                </ModuleRouteBoundary>
              }
            />
          ))}
          {/* Catch-all: a direct URL to a disabled module (its
              `routeBase/*` route is never mounted) or any unknown path
              falls through here. Renders a safe in-shell unavailable /
              not-found state instead of silently redirecting (PB-34). */}
          <Route path="*" element={<NotFoundRoute />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

/**
 * `<Route path>` is relative to its parent, so we drop the leading `/` from
 * `routeBase` (e.g. `/app/solve` → `app/solve`).
 */
function stripLeadingSlash(path: string): string {
  return path.startsWith("/") ? path.slice(1) : path;
}

function SessionPlayerAIProvider(
  props: ComponentProps<typeof PlayerAIProvider>,
) {
  const preferenceScope = useAuthStore(
    (state) =>
      `${state.claims?.tenantId ?? "anonymous"}:${state.claims?.userId ?? "anonymous"}`,
  );
  // Poll only when the live line is proven broken AND the tenant's setting —
  // or, while it is unset, the deployment default (ADR-0009) — says so. A
  // stable getter the client asks at each decision, so it is never rebuilt.
  const pollFallback = usePlayerAiPollFallback();
  const options = useMemo(
    () => ({ ...props.options, pollFallback }),
    [props.options, pollFallback],
  );
  return (
    <PlayerAIProvider key={preferenceScope} {...props} options={options} />
  );
}
